// Batch 0 (UX/IA overhaul) — one guiding empty-state.
//
// The audit found 10+ bespoke "No X yet" cards (and several lists with none).
// This standardizes the pattern: an icon, a title, an optional explanation,
// and an optional CTA that tells the operator what to do next — the enterprise
// convention of "empty-states that guide" rather than dead ends.
import * as React from "react";

import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

export interface EmptyStateProps {
  icon?: React.ComponentType<{ className?: string }>;
  title: string;
  description?: React.ReactNode;
  /** Optional CTA(s) — e.g. an "Add camera" button. */
  action?: React.ReactNode;
  /** Render inside a Card (default true). Set false to embed in a table cell. */
  card?: boolean;
  className?: string;
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  card = true,
  className,
}: EmptyStateProps) {
  const body = (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-2 py-10 text-center",
        className,
      )}
    >
      {Icon && <Icon className="h-8 w-8 text-muted-foreground/60" />}
      <p className="text-sm font-medium text-foreground">{title}</p>
      {description && (
        <p className="max-w-md text-xs text-muted-foreground">{description}</p>
      )}
      {action && <div className="mt-2 flex gap-2">{action}</div>}
    </div>
  );

  if (!card) return body;
  return (
    <Card>
      <CardContent className="p-0">{body}</CardContent>
    </Card>
  );
}
